import { Injectable } from '@angular/core';
import { AppUser } from '../users/user.model';
import { WorkflowApproval } from '../chat-widgets/approval-card/workflow-apporval.model';
import { PromoPlanHeader } from '../promo-plan/promo-plan.model';
import { CrudAction } from '../master-data/permissions/permission.model';

export interface ApprovalContext {
  customerId?: number;
  businessId?: number;
  businessUnitId?: number;
  branchId?: number;
  brandId?: number;
  supplierId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {

  constructor() { }

  /**
   * Checks if a user is authorized to act on a set of workflow approvals based on their roles and the approval context.
   * @param user The current user.
   * @param approvals The list of workflow approvals for a business object.
   * @param context The business context (e.g., customerId, businessId) of the object being approved.
   * @returns `true` if the user is authorized to act, `false` otherwise.
   */
  isAuthorizedToAct(user: AppUser | null, approvals: WorkflowApproval[] | undefined, context: ApprovalContext | null): boolean {
    if (!user || !approvals) {
      return false;
    }

    return approvals.some(approval => {
      if (!approval.isCurrentPending) {
        return false; // Not a pending action.
      }

      // 1. Direct user assignment check
      if (approval.approverUser?.id === user.id) {
        return true;
      }

      // 2. Role-based check
      if (approval.requiredRole && user.roles.some(userRole => userRole.id === approval.requiredRole!.id)) {
        // User has the required role, now check if their access is scoped.
        // An empty scope array (e.g., user.customerIds is empty/null) means they have access to all.
        const customerScopeMatch = !user.customerIds?.length || (context?.customerId !== undefined && user.customerIds.includes(context.customerId));
        const businessScopeMatch = !user.businessIds?.length || (context?.businessId !== undefined && user.businessIds.includes(context.businessId));
        const businessUnitScopeMatch = !user.businessUnitIds?.length || (context?.businessUnitId !== undefined && user.businessUnitIds.includes(context.businessUnitId));
        const branchScopeMatch = !user.branchIds?.length || (context?.branchId !== undefined && user.branchIds.includes(context.branchId));

        return customerScopeMatch && businessScopeMatch && businessUnitScopeMatch && branchScopeMatch;
      }

      return false;
    });
  }

  /**
   * Checks if a user is authorized to generate a settlement for a given promo plan.
   * The user must have the 'Sales' role and their access scopes must match the promo plan's entities.
   * @param user The current user.
   * @param promoPlanHeader The header of the promo plan.
   * @returns `true` if the user is authorized, `false` otherwise.
   */
  canGenerateSettlement(user: AppUser | null, promoPlanHeader: PromoPlanHeader | null): boolean {
    if (!user || !promoPlanHeader) {
      return false;
    }

    // 1. Check if user has the 'Sales' role.
    const hasSalesRole = user.roles.some(role => role.roleName === 'Sales');
    if (!hasSalesRole) {
      return false;
    }

    // 2. Check for scoped access. An empty scope array for a user (e.g., user.customerIds is empty) means access to all entities of that type.
    const customerScopeMatch = !user.customerIds?.length || (promoPlanHeader.customer?.id !== undefined && user.customerIds.includes(promoPlanHeader.customer.id));
    const businessScopeMatch = !user.businessIds?.length || (promoPlanHeader.business?.id !== undefined && user.businessIds.includes(promoPlanHeader.business.id));
    const businessUnitScopeMatch = !user.businessUnitIds?.length || (promoPlanHeader.businessUnit?.id !== undefined && user.businessUnitIds.includes(promoPlanHeader.businessUnit.id));
    // Note: Branch scope is not checked here as PromoPlanHeader does not contain branch information.

    return customerScopeMatch && businessScopeMatch && businessUnitScopeMatch;
  }

  /**
   * Checks if a user is authorized to upload an attachment for a given promo plan.
   * The user must have at least one role and their access scopes must match the promo plan's entities.
   * @param user The current user.
   * @param promoPlanHeader The header of the promo plan.
   * @returns `true` if the user is authorized, `false` otherwise.
   */
  canUploadAttachment(user: AppUser | null, promoPlanHeader: PromoPlanHeader | null): boolean {
    if (!user || !promoPlanHeader) {
      return false;
    }

    // 1. Check if user has any role.
    const hasAnyRole = user.roles && user.roles.length > 0;
    if (!hasAnyRole) {
      return false;
    }

    // 2. Check for scoped access. An empty scope array for a user (e.g., user.customerIds is empty) means access to all entities of that type.
    const customerScopeMatch = !user.customerIds?.length || (promoPlanHeader.customer?.id !== undefined && user.customerIds.includes(promoPlanHeader.customer.id));
    const businessScopeMatch = !user.businessIds?.length || (promoPlanHeader.business?.id !== undefined && user.businessIds.includes(promoPlanHeader.business.id));
    const businessUnitScopeMatch = !user.businessUnitIds?.length || (promoPlanHeader.businessUnit?.id !== undefined && user.businessUnitIds.includes(promoPlanHeader.businessUnit.id));

    return customerScopeMatch && businessScopeMatch && businessUnitScopeMatch;
  }

  /**
   * Checks if a user has a specific role.
   * @param user The current user.
   * @param roleName The name of the role to check for.
   * @returns `true` if the user has the role, `false` otherwise.
   */
  hasRole(user: AppUser | null, roleName: string): boolean {
    if (!user || !user.roles) {
      return false;
    }
    return user.roles.some(role => role.roleName === roleName);
  }

  /**
   * Checks if a user has at least one of the specified roles.
   * @param user The current user.
   * @param roleNames An array of role names to check for.
   * @returns `true` if the user has at least one of the roles, `false` otherwise.
   */
  hasAnyRole(user: AppUser | null, roleNames: string[]): boolean {
    if (!user || !user.roles) {
      return false;
    }
    const augmentedRoleNames = roleNames.includes('Admin') ? roleNames : [...roleNames, 'Admin'];
    return user.roles.some(userRole => augmentedRoleNames.includes(userRole.roleName));
  }


  /**
 * Checks if a user has at least one of the specified permissions for a given resource.
 * @param user The user to check.
 * @param resourceName The name of the resource (e.g., 'Settlement').
 * @param actions An array of actions to check for (e.g., ['create', 'update']).
 * @returns `true` if the user has at least one of the permissions, `false` otherwise.
 */
  public hasPermission(user: AppUser | null, resourceName: string, actions: CrudAction[]): boolean {
    if (!user || !user.roles) {
      return false;
    }
    if (this.hasRole(user, 'Admin')) {
      return true;
    }
    return user.roles.some(role =>
      role.permissions?.some(permission =>
        permission.resource.resourceName === resourceName && actions.includes(permission.action)
      )
    );
  }
}
